/**
 * A repeating quick action's run, answered from its notification or its chat.
 *
 * When the reminder behind a quick action fires, Ellie runs it on the server and drafts each
 * email to someone outside the plan in that plan chat (backend services/quick-actions/run). Each
 * draft is approved on its own: sent (as drafted, or edited first), or skipped. The server counts
 * sends-as-drafted per recipient toward "email them automatically".
 *
 * The notification only offers Send / Skip when the run drafted exactly one email; with more, it
 * opens the chat where the drafts are stacked. Send there sends exactly the draft — the same route
 * DraftCard uses, falling back to a PDF when the body carries health details.
 */

import { apiGet, apiProjectsPatch } from './apiClient';
import { parseResponseWithCharts, type DraftPayload } from './parseResponseWithCharts';
import { needsPdfAttachment } from './sendAsPdf';
import { sendMessage } from '../store/actions/messages';

export type ScheduledRunPushData = {
  quick_action_id?: string;
  query_id?: number | string | null;
  conversation_id?: string | null;
  project_id?: number | string;
  account_id?: string;
  cognito_id?: string;
  /** The recipient an automate offer is about. */
  to?: string;
};

type Outcome = { ok: true } | { ok: false; message: string };

const prefPath = (data: ScheduledRunPushData) => `/update-membership-pref/${data.project_id}/${data.account_id}/${data.cognito_id}`;

const patch = async (data: ScheduledRunPushData, body: Record<string, unknown>): Promise<Outcome> => {
  if (!data.quick_action_id || !data.project_id || !data.account_id || !data.cognito_id) return { ok: false, message: 'Missing quick action' };
  const res: any = await apiProjectsPatch(prefPath(data), { pref: 'quick_actions', ...body }).catch(() => null);
  return res?.success ? { ok: true } : { ok: false, message: res?.message || "Couldn't reach Ellie." };
};

/** The drafts in a run's reply that can be sent (they have an address). */
export function sendableDrafts(response: string): DraftPayload[] {
  return parseResponseWithCharts(response, true)
    .flatMap((segment) => (segment.type === 'draft' ? [segment.draftPayload] : []))
    .filter((draft) => !!draft.to && draft.to.includes('@'));
}

/** Send one draft as written (or as edited), in its chat; a PDF when the body carries health details. */
export async function sendDraftEmail(draft: DraftPayload, { projectId, conversationId }: { projectId: number | string; conversationId?: string | null }) {
  const send = (deliverAsPdf: boolean) => sendMessage(
    { subject: draft.subject?.trim() || '(no subject)', body: draft.body, project_id: projectId as any, attachments: [] } as any,
    [{ to_email: draft.to!.trim(), to_cognito: null, to_first: null, to_last: null } as any],
    [],
    {
      deliverAsPdf,
      ...(conversationId ? { conversationId } : {}),
      ...(draft.reply_to_message_id ? { replyToMessageId: draft.reply_to_message_id } : {}),
    },
  )();
  const first: any = await send(false);
  return needsPdfAttachment(first) ? send(true) : first;
}

/** The run's one draft, or null when there isn't exactly one (then the chat is where to answer). */
async function onlyDraft(data: ScheduledRunPushData): Promise<DraftPayload | null> {
  if (!data.conversation_id || data.query_id == null) return null;
  const conversation: any = await apiGet(
    `/prompts/get-conversation/${data.conversation_id}/${data.account_id}/${data.cognito_id}?project_id=${encodeURIComponent(String(data.project_id))}`,
  ).catch(() => null);
  const turn = (conversation?.payload || []).find((row: any) => String(row?.id) === String(data.query_id));
  const drafts = turn ? sendableDrafts(String(turn.response || '')) : [];
  return drafts.length === 1 ? drafts[0] : null;
}

/** A draft of a run was sent: as drafted counts toward automating that recipient; edited doesn't. */
export const recordDraftSent = (data: ScheduledRunPushData, to: string, edited = false) => (
  patch(data, { draft_sent: { id: data.quick_action_id, query_id: Number(data.query_id), to, edited } })
);
/** A draft of a run was skipped: that recipient's count starts over. */
export const recordDraftSkipped = (data: ScheduledRunPushData, to: string) => (
  patch(data, { draft_skipped: { id: data.quick_action_id, query_id: Number(data.query_id), to } })
);

/** The notification's Send: the run's one draft, as drafted. */
export async function sendScheduledRun(data: ScheduledRunPushData): Promise<Outcome> {
  const draft = await onlyDraft(data);
  if (!draft) return { ok: false, message: 'Open the chat to send it.' };
  const res: any = await sendDraftEmail(draft, { projectId: data.project_id!, conversationId: data.conversation_id });
  if (!res?.success) return { ok: false, message: res?.message || "Couldn't send it." };
  return recordDraftSent(data, draft.to!, false);
}

/** The notification's Skip: the run's one draft. */
export async function skipScheduledRun(data: ScheduledRunPushData): Promise<Outcome> {
  const draft = await onlyDraft(data);
  return draft ? recordDraftSkipped(data, draft.to!) : { ok: false, message: 'Open the chat to skip it.' };
}

export const automateScheduledAction = (data: ScheduledRunPushData) => patch(data, { set_auto: { id: data.quick_action_id, to: data.to, automatic: true } });
export const keepAskingScheduledAction = (data: ScheduledRunPushData) => patch(data, { decline_auto: { id: data.quick_action_id, to: data.to } });
export const askFirstScheduledAction = (data: ScheduledRunPushData) => patch(data, { ask_first: { id: data.quick_action_id } });
